import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { isNewShellRoute } from '../navigation/shell-topbar/section-label.util';
import { RoutePaths } from '../shared/api/types/enums';

/**
 * Chrome visibility is TWO independent flags since NBO-2207 (decision
 * 2026-09-02: the order/quote/request editor keeps the top bar and hides only
 * the rail, so the tenant/language/user cluster and the notifications bell
 * stay reachable while editing):
 *
 * - auth pages (sign-in, reset-password, 2FA) render bare: no rail, no top bar
 * - editor routes hide the rail only
 * - everything else renders full chrome
 */
export interface ChromeVisibility {
  /** true = do not render the accordion rail for the current route. */
  hideRail: boolean;
  /** true = do not render the top bar either (auth pages only). */
  hideTopbar: boolean;
  /**
   * true = the route has adopted the new shell (NBO-2390 Home first): the
   * scroll region paints the grey canvas and the 32px content inset is
   * dropped — the page owns its own spacing, straight under the top bar
   * (canon §2). Legacy pages keep the white ground and the inset, which is
   * what lets the shell ship before any listing is redesigned.
   */
  canvas: boolean;
}

/** Auth pages render with no chrome at all. */
export function isAuthUrl(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return (
    path.includes('/' + RoutePaths.SignIn) ||
    path.includes('/' + RoutePaths.ResetPassword) ||
    path.includes('/' + RoutePaths.MFA)
  );
}

/**
 * Editor detection is anchored on '/<segment>/' so listing routes (/sp/orders,
 * /bo/requests) keep their chrome while every editor route (/sp/orders/new,
 * /bo/orders/:id/edit, ...) matches.
 */
export function isEditorUrl(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return path.includes('/orders/') || path.includes('/quotes/') || path.includes('/requests/');
}

export function chromeVisibilityForUrl(url: string): ChromeVisibility {
  const bare = isAuthUrl(url);
  return {
    hideRail: bare || isEditorUrl(url),
    hideTopbar: bare,
    canvas: !bare && isNewShellRoute(url)
  };
}

/**
 * Navigation state of the application. Since the NBO-2331 shell rebuild this
 * service owns exactly one thing: chrome visibility for the current route.
 * The old two-tier sidenav machinery (selectedTab, SidenavMode, hover
 * open/close) died with the icon rail + secondary sidenav; the accordion rail
 * derives its own active/open state from the URL.
 */
export type NavigationState = ChromeVisibility;

@Injectable({
  providedIn: 'root'
})
export class NavigationService implements OnDestroy {
  private navigationStateSubject = new BehaviorSubject<NavigationState>(
    // Seed from the actual boot URL: on a cold new-tab open of a rail-less
    // route (editor via window.open, auth pages) the corrective NavigationEnd
    // is stalled behind auth guards + lazy chunks, and a hardcoded
    // everything-visible seed would paint the rail over the editor for that
    // whole window (NBO-2343)
    chromeVisibilityForUrl(window.location.pathname)
  );

  private destroySubject = new Subject<void>();

  constructor(private router: Router) {
    this.setupRouteChromeTracking();
  }

  /**
   * Observable that emits the complete navigation state
   */
  get navigationState$(): Observable<NavigationState> {
    return this.navigationStateSubject.asObservable();
  }

  /**
   * Updates the chrome visibility state (both flags together — a route is
   * always judged as a whole).
   */
  updateChromeVisibility(visibility: ChromeVisibility): void {
    this.navigationStateSubject.next({ ...visibility });
  }

  /**
   * Keeps chrome visibility in sync with the current route. Deliberately a
   * dedicated subscription (NBO-2343): it used to live in AppComponent's
   * page-title pipe, where any error in the title/translate chain tore down
   * chrome tracking for the rest of the session. urlAfterRedirects so a guard
   * redirect (e.g. ** -> /404) is judged by the URL the user actually lands on.
   */
  private setupRouteChromeTracking(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroySubject)
      )
      .subscribe((event) => {
        try {
          this.updateChromeVisibility(chromeVisibilityForUrl(event.urlAfterRedirects || event.url));
        } catch (error) {
          console.error('Error tracking route chrome state:', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
